import { useCallback, useEffect, useMemo, useState } from 'react'

import { COL_SCHEDULE_SLOTS } from '../../config/collections'
import { ShiftFilterSelect } from '../../components/ShiftFilterSelect'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import type { CsvColumn } from '../../lib/exportCsv'
import { useAssignedShiftFilter } from '../../hooks/useAssignedShiftFilter'
import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  aggregateMaterialRequirements,
  hasMaterialDeficit,
  loadBomByProductIds,
  type MaterialRequirementRow,
} from '../../lib/materialRequirements'
import { pb } from '../../pocketbase'
import { formatDateRu, formatQty } from '../../lib/scheduleCalculations'
import { unitLabel } from '../../lib/units'
import type { ScheduleSlotRecord } from '../../types/records'
import { Alert } from '../../components/ui'
import {
  btn,
  cn,
  inputClass,
  loadingClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'

function dateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function MaterialsPage() {
  const { shiftId, setShiftId, shifts, canAllShifts, shiftLocked, appendShiftFilter } =
    useAssignedShiftFilter()
  const [fromDate, setFromDate] = useState(() => dateInputValue(new Date()))
  const [toDate, setToDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 7)
    return dateInputValue(d)
  })
  const [rows, setRows] = useState<MaterialRequirementRow[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const filter = appendShiftFilter(
        `plannedStart >= "${fromDate} 00:00:00" && plannedStart <= "${toDate} 23:59:59" && status != "cancelled"`,
      )
      const slots = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
        filter,
        expand: 'requestLine.product',
        sort: 'plannedStart',
      })
      const productIds = slots
        .map((s) => s.expand?.requestLine?.product)
        .filter((id): id is string => typeof id === 'string')
      const bomByProduct = await loadBomByProductIds(productIds)
      setRows(aggregateMaterialRequirements(slots, bomByProduct))
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка расчёта MRP'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [fromDate, toDate, appendShiftFilter])

  const mrpCsvColumns: CsvColumn<MaterialRequirementRow>[] = useMemo(
    () => [
      { header: 'Расходник', value: (r) => r.consumable?.name ?? '' },
      { header: 'Код', value: (r) => r.consumable?.code ?? '' },
      { header: 'Ед.', value: (r) => (r.consumable ? unitLabel(r.consumable.unit) : '') },
      { header: 'Потребность', value: (r) => r.requiredQty },
      { header: 'Остаток', value: (r) => r.stockQty },
      { header: 'Дефицит', value: (r) => r.deficit },
    ],
    [],
  )

  useEffect(() => {
    void load()
  }, [load])

  const deficitCount = useMemo(() => rows.filter((r) => r.deficit > 0).length, [rows])
  const belowMinCount = useMemo(
    () => rows.filter((r) => {
      const min = Number(r.consumable?.minStock) || 0
      return min > 0 && r.stockQty < min
    }).length,
    [rows],
  )

  if (loading && rows.length === 0) {
    return <div className={loadingClass}>Расчёт потребности…</div>
  }

  return (
    <div>
      <p className="mb-4 text-sm text-su-text">
        MRP-lite: потребность в сырье по графику (слоты × BOM). Без движений по складу — сравнение с текущим остатком.
      </p>
      {err && <Alert kind="err">{err}</Alert>}
      {hasMaterialDeficit(rows) && (
        <Alert kind="warn">
          Дефицит по {deficitCount} позициям на выбранный период. Ниже мин. запаса: {belowMinCount}.
        </Alert>
      )}

      <div className={toolbarClass}>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-su-text">С</span>
          <input type="date" className={inputClass} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-su-text">По</span>
          <input type="date" className={inputClass} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Пересчитать
        </button>
        <ShiftFilterSelect
          shiftId={shiftId}
          shifts={shifts}
          canAllShifts={canAllShifts}
          locked={shiftLocked}
          onChange={setShiftId}
        />
        {rows.length > 0 && (
          <ExportCsvButton filename="mrp-materials" rows={rows} columns={mrpCsvColumns} />
        )}
      </div>

      <p className="mb-3 text-xs text-su-text">
        Период: {formatDateRu(`${fromDate}T12:00:00`)} — {formatDateRu(`${toDate}T12:00:00`)} · позиций: {rows.length}
      </p>

      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Расходник</th>
              <th className={tableHeadClass}>Код</th>
              <th className={tableHeadClass}>Потребность</th>
              <th className={tableHeadClass}>Остаток</th>
              <th className={tableHeadClass}>Мин.</th>
              <th className={tableHeadClass}>Дефицит</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={6}>
                  <em>Нет потребности — нет слотов в графике или BOM пуст</em>
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const minStock = Number(r.consumable?.minStock) || 0
              const belowMin = minStock > 0 && r.stockQty < minStock
              const unit = r.consumable?.unit ?? ''
              return (
                <tr
                  key={r.consumableId}
                  className={cn(
                    tableRowHoverClass,
                    r.deficit > 0 && 'bg-red-50/70 dark:bg-red-950/20',
                    belowMin && r.deficit <= 0 && 'bg-amber-50/70 dark:bg-amber-950/20',
                  )}
                >
                  <td className={tableCellClass}>{r.consumable?.name ?? r.consumableId}</td>
                  <td className={tableCellClass}>{r.consumable?.code ?? '—'}</td>
                  <td className={tableCellClass}>
                    {formatQty(r.requiredQty)} {unitLabel(unit)}
                  </td>
                  <td className={tableCellClass}>
                    {formatQty(r.stockQty)} {unitLabel(unit)}
                  </td>
                  <td className={tableCellClass}>
                    {minStock > 0 ? `${formatQty(minStock)} ${unitLabel(unit)}` : '—'}
                  </td>
                  <td className={cn(tableCellClass, r.deficit > 0 && 'font-semibold text-red-700 dark:text-red-300')}>
                    {r.deficit > 0 ? `${formatQty(r.deficit)} ${unitLabel(unit)}` : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
